import { Area, AreaChart, Line, LineChart, ReferenceLine, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { usd } from "@/components/dash/utils";

const fmtT = (t) => new Date(t * 1000).toLocaleTimeString([], { minute: "2-digit", second: "2-digit" });

export const LiveTracker = ({ live, lp }) => {
  if (!live) return null;
  const start = live.btc.start;
  const price = lp?.chainlink?.price ?? live.btc.price;
  const data = [...(live.history || [])];
  const lastT = data.length ? data[data.length - 1].t : 0;
  if (lp?.chainlink && lp.chainlink.ts > lastT) data.push({ t: Math.floor(lp.chainlink.ts), price: +price.toFixed(2) });
  const diff = price - start, above = diff >= 0;
  const prices = data.map((d) => d.price).concat(start);
  const pad = Math.max((Math.max(...prices) - Math.min(...prices)) * 0.2, 5);
  const domain = [Math.min(...prices) - pad, Math.max(...prices) + pad];
  const color = above ? "#34D399" : "#F87171";

  return (
    <section className="card p-5 sm:p-6 fade-up" data-testid="live-tracker">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="flex items-center gap-2 text-xs uppercase tracking-widest font-num text-slate-400">
            <span className="w-2 h-2 rounded-full bg-red-500 pulse-dot" /> Live tracker · this 15-min window
          </div>
          <div className={`font-num font-bold text-3xl sm:text-4xl mt-1 ${above ? "text-[#34D399]" : "text-[#F87171]"}`} data-testid="tracker-price">{usd(price)}</div>
          <div className="text-xs text-slate-400 mt-1" data-testid="tracker-diff">
            {above ? "▲" : "▼"} {usd(Math.abs(diff))} {above ? "above" : "below"} the price to beat ({usd(start)}) — <b className="text-slate-200">{above ? "UP is winning right now" : "DOWN is winning right now"}</b>
          </div>
        </div>
        <div className="text-[11px] text-slate-500 text-right">BRTI price (CF Benchmarks)<br />what Polymarket US settles on · every 2s</div>
      </div>
      <div className="h-48 mt-4" data-testid="tracker-price-chart">
        <ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={100}>
          <AreaChart data={data} margin={{ top: 5, right: 5, left: 0, bottom: 0 }}>
            <defs>
              <linearGradient id="pg" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={color} stopOpacity={0.3} /><stop offset="100%" stopColor={color} stopOpacity={0} />
              </linearGradient>
            </defs>
            <XAxis dataKey="t" tickFormatter={fmtT} stroke="#475569" fontSize={10} minTickGap={40} />
            <YAxis domain={domain} stroke="#475569" fontSize={10} width={60} tickFormatter={(v) => `$${Math.round(v).toLocaleString()}`} />
            <Tooltip contentStyle={{ background: "#0B1220", border: "1px solid #1A2338", fontSize: 12 }} labelFormatter={fmtT} formatter={(v) => [usd(v), "BTC"]} />
            <ReferenceLine y={start} stroke="#FBBF24" strokeDasharray="4 4" label={{ value: "Price to beat", fill: "#FBBF24", fontSize: 10, position: "insideTopLeft" }} />
            <Area type="monotone" dataKey="price" stroke={color} fill="url(#pg)" strokeWidth={2} isAnimationActive={false} />
          </AreaChart>
        </ResponsiveContainer>
      </div>
      <div className="text-[10px] uppercase tracking-widest font-num text-slate-400 mt-5">Odds over this window (price of Up vs Down)</div>
      <div className="h-32 mt-2" data-testid="tracker-odds-chart">
        <ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={100}>
          <LineChart data={live.history || []} margin={{ top: 5, right: 5, left: 0, bottom: 0 }}>
            <XAxis dataKey="t" tickFormatter={fmtT} stroke="#475569" fontSize={10} minTickGap={40} />
            <YAxis domain={[0, 1]} stroke="#475569" fontSize={10} width={60} tickFormatter={(v) => `${Math.round(v * 100)}¢`} />
            <Tooltip contentStyle={{ background: "#0B1220", border: "1px solid #1A2338", fontSize: 12 }} labelFormatter={fmtT} formatter={(v, n) => [`${Math.round(v * 100)}¢`, n]} />
            <ReferenceLine y={0.5} stroke="#334155" strokeDasharray="3 3" />
            <Line type="monotone" dataKey="up" name="Up" stroke="#34D399" dot={false} strokeWidth={2} isAnimationActive={false} />
            <Line type="monotone" dataKey="down" name="Down" stroke="#F87171" dot={false} strokeWidth={2} isAnimationActive={false} />
            <Line type="monotone" dataKey="model_up" name="Model Up estimate" stroke="#22D3EE" strokeDasharray="4 3" dot={false} strokeWidth={1.5} isAnimationActive={false} />
          </LineChart>
        </ResponsiveContainer>
      </div>
      <p className="text-[11px] text-slate-500 mt-2">Green = cost of Up, red = cost of Down, blue dashed = the bot's own estimate for Up. When blue sits well above green (or well below it, for Down), the bot sees a bargain.</p>
    </section>
  );
};
